import { useDaaSEditableEntities } from '@internal/plugin-azure-resources-common-react';
import { useEffect, useMemo } from 'react';
import { useAsync } from 'react-use';

import { stringifyEntityRef } from '@backstage/catalog-model';
import { errorApiRef, useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';

import { useGetUserGroups } from '../useGetUserGroups';

export const useGetDaasEntities = (
  entityType: string,
  userOwedEntities: boolean = false,
  userInSecurityGroup: boolean = false,
) => {
  const errorApi = useApi(errorApiRef);
  const catalogApi = useApi(catalogApiRef);

  const {
    value: userGroups,
    loading: userGroupsLoading,
    error: userGroupsError,
  } = useGetUserGroups();

  const {
    editableRefs,
    loading: editableLoading,
    error: editableError,
  } = useDaaSEditableEntities(entityType, { enabled: userInSecurityGroup });

  const {
    value: entities,
    loading,
    error,
  } = useAsync(async () => {
    const fields = [
      'metadata.title',
      'metadata.name',
      'metadata.namespace',
      'metadata.description',
      'metadata.annotations',
      'kind',
      'spec',
      'relations',
    ];

    if (userOwedEntities) {
      if (userGroupsLoading) return [];

      if (userGroupsError) {
        errorApi.post(userGroupsError);
        return [];
      }

      if (!userGroups || userGroups.length === 0) {
        return [];
      }

      const groupEntities = userGroups.map((group: any) =>
        catalogApi.getEntities({
          filter: {
            kind: 'Component',
            'spec.type': entityType,
            'spec.owner': group,
          },
          fields,
        }),
      );

      const promises = await Promise.all(groupEntities);
      const items = promises.flatMap((result: any) => result.items);

      return items;
    }

    const { items } = await catalogApi.getEntities({
      filter: {
        kind: 'Component',
        'spec.type': entityType,
      },
      fields,
    });

    return items;
  }, [
    entityType,
    userOwedEntities,
    userGroupsLoading,
    userGroups,
    userGroupsError,
  ]);

  useEffect(() => {
    if (userInSecurityGroup && editableError) {
      errorApi.post(editableError);
    }
  }, [userInSecurityGroup, editableError, errorApi]);

  // When userInSecurityGroup is enabled, keep only the entities the daas-backend
  // reports the caller may edit. Fails closed: empty while still resolving or on
  // error.
  const filteredEntities = useMemo(() => {
    if (!userInSecurityGroup || !entities) return entities;
    if (editableLoading) return undefined;
    if (editableError) return [];
    return entities.filter(entity =>
      editableRefs.has(stringifyEntityRef(entity)),
    );
  }, [
    userInSecurityGroup,
    entities,
    editableLoading,
    editableError,
    editableRefs,
  ]);

  return {
    value: userInSecurityGroup ? filteredEntities : entities,
    loading: loading || (userInSecurityGroup && editableLoading),
    error: error || editableError,
  };
};
